import { CloseOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import { defaultCategory } from 'config/defaultCategory';
import { useManagePermittedRoles } from 'library/hooks';
import _ from 'lodash';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectDeviceFieldsByDeviceID, selectDevicesByDeviceID } from 'reduxStore/devices/selectors';
import { devicesActions } from 'reduxStore/devices/slice';
import { Loader } from 'zComponents/atoms';
import { EditableDeviceField } from 'zComponents/molecules';
import { Button, Col, Empty, Flex, Form, Row, Tooltip, Typography } from 'zui/Antd';

const { Text } = Typography;

const AdditionalInformationPanelContent = ({ deviceID }) => {
	// Hooks
	const [form] = Form.useForm();
	const {
		token: { fontSizeLG, colorTextLabel, colorTextDescription, paddingMD, marginMD, fontSize, paddingXXS }
	} = theme.useToken();
	const dispatch = useDispatch();
	const canManageDeviceFields = useManagePermittedRoles('updateDevice');

	// Redux state
	const { createDeviceFieldLoading, deleteDeviceFieldLoading } = useSelector((state) => state.Devices);
	const device = useSelector((state) => selectDevicesByDeviceID(state, deviceID));
	const deviceFields = useSelector((state) => selectDeviceFieldsByDeviceID(state, deviceID));

	// Category configuration for additional fields
	const categoryConfig = useMemo(() => {
		return defaultCategory[device?.defaultCategoryID] || null;
	}, [device?.defaultCategoryID]);

	// Determine if device is in Health & Safety category
	//? (allows all fields to be edited/deleted)
	const isHealthAndSafety = useMemo(() => {
		return device?.defaultCategoryID === 1;
	}, [device?.defaultCategoryID]);

	// Map device fields to a more usable format
	//? If showOnAdditionalFields is defined, filter fields accordingly
	//? If not defined, show all fields
	//? If Health & Safety, show all fields
	const existingFields = useMemo(() => {
		let mapped = deviceFields.map((field) => ({
			fieldID: field.fieldID,
			fieldTitle: field.fieldTitle,
			fieldTypeID: field.fieldTypeID,
			value: field.value
		}));

		const showFields = categoryConfig?.showOnAdditionalFields;
		if (!isHealthAndSafety && Array.isArray(showFields) && showFields.length > 0) {
			mapped = mapped.filter((field) => showFields.includes(field.fieldTitle));
		}

		return mapped;
	}, [deviceFields, categoryConfig, isHealthAndSafety]);

	// Sync form values when existingFields change (e.g., after modal update)
	useEffect(() => {
		const formValues = {};
		existingFields.forEach((field) => {
			formValues[`field_${field.fieldID}`] = field.value;
		});
		form.setFieldsValue(formValues);
	}, [existingFields, form]);

	// Check if field can be edited or deleted based on category configuration
	// Otherwise, check if field is in editableCustomFields
	const canEditOrDelete = (fieldTitle) => {
		if (!categoryConfig || isHealthAndSafety) return true;
		const editable = categoryConfig?.editableCustomFields;
		if (!Array.isArray(editable)) return false;
		if (editable.length === 0) return false;
		return editable.includes(fieldTitle);
	};

	// Handle field deletion
	const handleDeleteField = (fieldID) => {
		if (!canManageDeviceFields) return;
		dispatch(devicesActions.deleteDeviceField({ deviceID, fieldID }));
	};

	// Dispatch update field action (called on blur/enter)
	const dispatchUpdate = useCallback(
		(newVal, fieldID, originalValue) => {
			const newValue = typeof newVal === 'string' ? newVal.trim() : newVal;

			if (!canManageDeviceFields) return;
			if (!_.isEqual(newValue, originalValue) && !_.isNil(newValue) && newValue !== '') {
				dispatch(
					devicesActions.updateDeviceField({
						deviceID,
						data: { fields: [{ fieldID, value: newValue }] }
					})
				);
			}
		},
		[dispatch, deviceID, canManageDeviceFields]
	);

	return (
		<Form form={form}>
			<Flex vertical>
				{(createDeviceFieldLoading || deleteDeviceFieldLoading) && existingFields.length === 0 ? (
					<Loader />
				) : existingFields.length === 0 ? (
					<Empty
						description='No custom fields yet'
						image={Empty.PRESENTED_IMAGE_SIMPLE}
						style={{ margin: '16px 0' }}
					/>
				) : (
					existingFields.map((field) => {
						const { fieldID, fieldTitle, fieldTypeID, value } = field;
						return (
							<Row
								key={fieldID}
								gutter={[paddingMD, paddingMD]}
								style={{ marginBottom: marginMD }}
								wrap
								justify='space-between'
								align='middle'
							>
								<Col xs={24} md={12}>
									<Text strong style={{ color: colorTextLabel, fontSize: fontSizeLG }}>
										{fieldTitle}
									</Text>
								</Col>

								<Col style={{ position: 'relative' }} xs={24} md={12}>
									<Flex justify='space-between' align='center'>
										<Form.Item
											name={`field_${fieldID}`}
											initialValue={value}
											rules={[{ required: true, message: 'This field cannot be empty' }]}
											style={{ marginBottom: 0, flex: 1 }}
										>
											<EditableDeviceField
												key={fieldID}
												fieldTypeID={fieldTypeID}
												value={value}
												disabled={!canManageDeviceFields || !canEditOrDelete(field.fieldTitle)}
												onChangeInput={(val) => dispatchUpdate(val, fieldID, value)}
											/>
										</Form.Item>
										{canManageDeviceFields && canEditOrDelete(field.fieldTitle) && (
											<Tooltip title='Delete Field'>
												<Button
													type='text'
													style={{ marginLeft: paddingXXS }}
													icon={
														<CloseOutlined
															style={{ fontSize: fontSize, color: colorTextDescription }}
														/>
													}
													onClick={() => handleDeleteField(fieldID)}
													disabled={createDeviceFieldLoading}
												/>
											</Tooltip>
										)}
									</Flex>
								</Col>
							</Row>
						);
					})
				)}
			</Flex>
		</Form>
	);
};

export default AdditionalInformationPanelContent;
